import axios from 'axios';
import { formatApiError } from '@/services/api';
import { scheduledInstancesService } from '@/services/scheduled-instances.service';
import type { ScheduledInstanceStatus, ShootData } from '@/types/scheduled-instances';

/**
 * Shoot error recovery (shared by usePostComposer + useVariationComposer).
 *
 * A shoot can fully commit server-side while the HTTP response is lost in
 * transit. The backend is idempotent-safe: a retry of an in-flight mapping
 * returns 409 `publish_in_progress`. (`already_shot` is a terminal outcome that may be a failure, so it is NOT softened.)
 * This helper never declares success unless the server positively confirms it.
 */

/**
 * Only `publish_in_progress` is benign (a live instance exists). `already_shot`
 * is deliberately NOT here: it can mean FAILED/MISSED/PARTIAL, so it must surface
 * the backend's accurate message through the normal error path.
 */
export const SHOOT_ALREADY_HANDLED_CODES: readonly string[] = ['publish_in_progress'];

export const SHOOT_ALREADY_SCHEDULED_MESSAGE = 'This post is already scheduled.';

export const SHOOT_UNCONFIRMED_MESSAGE =
  "We couldn't confirm the schedule. Please check your connection and reload before trying again.";

/** Single source for "live" instance statuses (mirrors backend GENUINELY_PENDING_INSTANCE_STATUSES). */
const LIVE_INSTANCE_STATUSES: readonly ScheduledInstanceStatus[] = ['QUEUED', 'PICKED_UP', 'PUBLISHING'];

/** Statuses that positively prove a shoot committed: live, or already published. */
const CONFIRMING_INSTANCE_STATUSES: readonly ScheduledInstanceStatus[] = [...LIVE_INSTANCE_STATUSES, 'PUBLISHED'];

/** Tolerance when matching an instance's scheduledAt to the requested shoot time. */
const SCHEDULED_AT_TOLERANCE_MS = 2 * 60 * 1000;

const RECHECK_DELAY_MS = 1000;

export type ShootErrorKind = 'network' | 'already_scheduled' | 'other';

/** Classify a shoot failure. Reuses formatApiError (SSOT) for codes. */
export function classifyShootError(error: unknown): ShootErrorKind {
  const { code } = formatApiError(error);
  if (code && SHOOT_ALREADY_HANDLED_CODES.includes(code)) return 'already_scheduled';
  // No HTTP response at all (dropped connection / timeout): the write may have committed.
  if (code === 'NETWORK_ERROR' || code === 'TIMEOUT') return 'network';
  return 'other';
}

/** Thrown when a network-dropped shoot could not be confirmed as scheduled. */
export class ShootUnconfirmedError extends Error {
  constructor() {
    super(SHOOT_UNCONFIRMED_MESSAGE);
    this.name = 'ShootUnconfirmedError';
  }
}

export type ShootOutcome = 'shot' | 'recovered' | 'already_scheduled';

export interface ShootResult {
  outcome: ShootOutcome;
  /** For 'already_scheduled': the live instance's actual scheduledAt (ISO), if readable. */
  liveScheduledAt: string | null;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Message for the publish_in_progress case. `formatDateTime` must be the
 * timezone-cascade formatter from useTimezone().
 */
export function buildAlreadyScheduledMessage(
  liveScheduledAt: string | null,
  formatDateTime: (date: string | Date | null | undefined) => string
): string {
  if (!liveScheduledAt) return SHOOT_ALREADY_SCHEDULED_MESSAGE;
  const formatted = formatDateTime(liveScheduledAt);
  return formatted ? `This post is already scheduled for ${formatted}.` : SHOOT_ALREADY_SCHEDULED_MESSAGE;
}

/** Read the live instance's scheduledAt; null if none or unreadable. GET only. */
async function readLiveScheduledAt(calendarMappingId: number): Promise<string | null> {
  try {
    const instances = await scheduledInstancesService.listInstances({ calendarMappingId });
    const live = instances.find((i) => LIVE_INSTANCE_STATUSES.includes(i.status));
    return live?.scheduledAt ?? null;
  } catch {
    return null;
  }
}

/**
 * Single read-only check: did THIS shoot commit? Requires a positive instance
 * (live or PUBLISHED) whose scheduledAt matches the requested time (+/-2 min), or,
 * for an immediate shoot, falls within the last ~2 min. mapping.status is a stale
 * cache and is intentionally not trusted.
 */
async function isShootConfirmed(data: ShootData): Promise<boolean> {
  try {
    const instances = await scheduledInstancesService.listInstances({ calendarMappingId: data.calendarMappingId });
    const now = Date.now();
    const requestedMs = new Date(data.scheduledAt).getTime();
    return instances.some((i) => {
      if (!CONFIRMING_INSTANCE_STATUSES.includes(i.status)) return false;
      const at = new Date(i.scheduledAt).getTime();
      if (Number.isNaN(at)) return false;
      if (data.immediate) {
        return at >= now - SCHEDULED_AT_TOLERANCE_MS && at <= now + SCHEDULED_AT_TOLERANCE_MS;
      }
      return !Number.isNaN(requestedMs) && Math.abs(at - requestedMs) <= SCHEDULED_AT_TOLERANCE_MS;
    });
  } catch {
    // Can't read = can't confirm. Never treat as success.
    return false;
  }
}

/**
 * Run a shoot with recovery:
 *  - success                         -> 'shot'
 *  - 409 publish_in_progress         -> 'already_scheduled' (+ the live instance's time)
 *  - 409 already_shot and all else   -> rethrown untouched (accurate backend message)
 *  - network error: re-read instances once (+ one ~1s retry); positively matched -> 'recovered',
 *    otherwise throws ShootUnconfirmedError. Never re-POSTs.
 */
export async function shootWithRecovery<T extends ShootData>(
  data: T,
  doShoot: (data: T) => Promise<unknown>
): Promise<ShootResult> {
  try {
    await doShoot(data);
    return { outcome: 'shot', liveScheduledAt: null };
  } catch (error) {
    const kind = classifyShootError(error);
    if (kind === 'already_scheduled') {
      return { outcome: 'already_scheduled', liveScheduledAt: await readLiveScheduledAt(data.calendarMappingId) };
    }
    if (kind !== 'network') throw error;

    if (await isShootConfirmed(data)) return { outcome: 'recovered', liveScheduledAt: null };
    await sleep(RECHECK_DELAY_MS);
    if (await isShootConfirmed(data)) return { outcome: 'recovered', liveScheduledAt: null };
    throw new ShootUnconfirmedError();
  }
}

/**
 * Backend 409 `already_scheduled`: the same post/variation + channel already has a
 * live instance on the same day (on another mapping). Not a failure of the user's
 * input: they must change the time or cancel the other schedule. The composer keeps
 * all state (nothing is cleared on this path).
 */
export const ALREADY_SCHEDULED_CONFLICT_CODE = 'already_scheduled';

export interface AlreadyScheduledConflict {
  channelName: string;
  existingScheduledAt: string;
  /** Backend's English message, used only as a fallback. */
  fallbackMessage: string;
}

/** Extract the conflict details from a 409 already_scheduled error, else null. */
export function getAlreadyScheduledConflict(error: unknown): AlreadyScheduledConflict | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const data: unknown = error.response.data;
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.code !== ALREADY_SCHEDULED_CONFLICT_CODE) return null;
  return {
    channelName: typeof body.channelName === 'string' ? body.channelName : '',
    existingScheduledAt: typeof body.existingScheduledAt === 'string' ? body.existingScheduledAt : '',
    fallbackMessage: formatApiError(error).message,
  };
}

/**
 * Localised conflict message. `formatDateTime` must be the timezone-cascade
 * formatter from useTimezone(). Falls back to the backend message if details are missing.
 */
export function buildAlreadyScheduledConflictMessage(
  conflict: AlreadyScheduledConflict,
  t: (key: 'already_scheduled_conflict', options: { channel: string; datetime: string }) => string,
  formatDateTime: (date: string | Date | null | undefined) => string
): string {
  const datetime = conflict.existingScheduledAt ? formatDateTime(conflict.existingScheduledAt) : '';
  if (!conflict.channelName || !datetime) return conflict.fallbackMessage;
  return t('already_scheduled_conflict', { channel: conflict.channelName, datetime });
}

export interface SubmitWarning {
  severity: 'warning';
  message: string;
  /** 'unconfirmed' = network-dropped shoot we could not verify; 'conflict' = 409 already_scheduled. */
  kind: 'unconfirmed' | 'conflict';
}

/**
 * Single classifier for every composer submit catch. Returns an amber warning for the
 * soft outcomes (unconfirmed shoot, same-day already_scheduled 409), else null so the
 * caller falls back to its generic red error. Callers must keep the user's input
 * (no reset/close) when a warning is returned.
 */
export function classifySubmitWarning(
  error: unknown,
  t: (key: 'already_scheduled_conflict', options: { channel: string; datetime: string }) => string,
  formatDateTime: (date: string | Date | null | undefined) => string
): SubmitWarning | null {
  if (error instanceof ShootUnconfirmedError) {
    return { severity: 'warning', message: error.message, kind: 'unconfirmed' };
  }
  const conflict = getAlreadyScheduledConflict(error);
  if (conflict) {
    return { severity: 'warning', message: buildAlreadyScheduledConflictMessage(conflict, t, formatDateTime), kind: 'conflict' };
  }
  return null;
}
